"use client";

import Link from "next/link";
import { Fragment, useState, FormEvent, type ReactNode } from "react";
import type { PublicProductFilterOptionsDto, PublicUsageAreaDto } from "@/lib/api-client";
import { createTranslator } from "@/lib/localization";
import { SafeImage, type ResolvedDefaultImages } from "@/components/common/SafeImage";
import { filterButtonAttributes, resolveFilterLayout, type FilterKey, type FilterLayout } from "@/lib/filter-registry";
import type { FilterButtonDesign } from "@/lib/theme-settings";
import { homeHref } from "@/lib/storefront-links";
import type { ProductCardViewModel } from "@/lib/storefront-view-models";

type SelectedFilters = {
  categories: string[];
  subcategories: string[];
  regionNames: string[];
  productName?: string;
  colorIds: string[];
  sizes: string[];
  minPrice?: string;
  maxPrice?: string;
  usageArea?: string;
  technical: Record<string, string[]>;
};

type Props = {
  /** Card view models built on the SERVER (ShopFiltersTemplate) with the domain's presentation applied: when the domain hides
   * prices there is no price data in them at all, so nothing price-related can reach this client component or the page payload. */
  products: ProductCardViewModel[];
  filterOptions: PublicProductFilterOptionsDto;
  usageAreas: PublicUsageAreaDto[];
  selected: SelectedFilters;
  activeLanguage: string;
  basePath?: string;
  texts?: Record<string, string> | null;
  defaults?: ResolvedDefaultImages;
  /** Which registry filters render, in order — ALREADY resolved and sanitized on the server from the domain's
   * public filter configuration (lib/filter-registry.ts), so no raw configuration ever reaches the browser.
   * Undefined = registry defaults = exactly the filters the sidebar always showed. It only decides which
   * CONTROLS render — the URL is parsed and executed by the server exactly as before, so a filter that is
   * hidden here but present in the URL still filters the results. */
  filterLayout?: FilterLayout;
  /** Colors of the PRIMARY action ("Apply", desktop sidebar and mobile sheet) only — validated, or nothing. */
  buttonDesign?: FilterButtonDesign;
};

export function ShopWithSidebar({ products, filterOptions, usageAreas, selected, activeLanguage, basePath, texts, defaults, filterLayout, buttonDesign }: Props) {
  const t = createTranslator(texts ?? {});
  const cleanUrl = basePath || homeHref(activeLanguage);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<"latest" | "best">("latest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(selected.categories)
  );

  const displayedProducts = [...products].sort((a, b) => {
      if (sortBy === "best") {
        return (b.discountPercent ?? 0) - (a.discountPercent ?? 0);
      }
      return 0;
    });

    const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDesktopCategoriesCollapsed, setIsDesktopCategoriesCollapsed] = useState(false);
  const [isMobileCategoriesCollapsed, setIsMobileCategoriesCollapsed] = useState(false);

  const closeFilter = () => {
    setIsFilterOpen(false);
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.currentTarget);
    const params = new URLSearchParams();

    for (const [key, value] of formData) {
      if (value) params.append(key, String(value));
    }

    const queryString = params.toString();
    const newUrl = queryString ? `${cleanUrl}?${queryString}` : cleanUrl;
    window.location.href = newUrl;
  };

  const handleClearFilters = () => {
    setIsLoading(true);
    window.location.href = cleanUrl;
  };

  const toggleCategory = (slug: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(slug)) {
      newExpanded.delete(slug);
    } else {
      newExpanded.add(slug);
    }
    setExpandedCategories(newExpanded);
  };

  // effective domain configuration ∩ Shop-supported filters → enabled → ordered. Desktop sidebar and mobile
  // sheet both render THIS layout, so they can never disagree about which filters exist or in which order.
  const layout = filterLayout ?? resolveFilterLayout("shop", null);

  // One renderer per Shop-supported registry key, shared by the desktop sidebar and the mobile sheet. The markup
  // is the original markup moved as-is; the two variants differ only in the id prefix and the collapse state
  // (exactly as the duplicated blocks did before).
  const renderShopFilter = (key: FilterKey, variant: "desktop" | "mobile"): ReactNode => {
    const p = variant === "mobile" ? "mobile-" : "";
    const categoriesCollapsed = variant === "mobile" ? isMobileCategoriesCollapsed : isDesktopCategoriesCollapsed;
    const toggleCategoriesCollapsed = () =>
      variant === "mobile" ? setIsMobileCategoriesCollapsed(!isMobileCategoriesCollapsed) : setIsDesktopCategoriesCollapsed(!isDesktopCategoriesCollapsed);

    switch (key) {
      case "category":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <button
              type="button"
              onClick={toggleCategoriesCollapsed}
              className="w-full mb-3 flex items-center justify-between hover:opacity-80 transition-opacity"
            >
              <h3 className="text-sm font-semibold text-dark">{t("shop.categories")}</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-dark-4">{filterOptions.categories.length}</span>
                <svg
                  className={`h-4 w-4 transition-transform ${categoriesCollapsed ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </div>
            </button>

            {!categoriesCollapsed && (
            <div className="space-y-2">
              {filterOptions.categories.map((category) => {
                const isExpanded = expandedCategories.has(category.slug);
                // Subcategories are nested inside Category; the "subcategory" registry key switches them on/off.
                const categorySubcategories = layout.showSubcategories
                  ? filterOptions.subcategories.filter((sub) => sub.parentSlug === category.slug)
                  : [];

                return (
                  <div key={category.slug} className="border-b border-gray-3 pb-2 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <label htmlFor={`${p}cat-${category.slug}`} className="flex min-w-0 items-center gap-2 text-sm text-dark-2 cursor-pointer hover:text-dark">
                        <input
                          type="checkbox"
                          name="categories"
                          value={category.slug}
                          id={`${p}cat-${category.slug}`}
                          defaultChecked={selected.categories.includes(category.slug)}
                          className="cursor-pointer"
                        />
                        <span className="truncate">{category.name}</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-dark-4">{category.count}</span>
                        {categorySubcategories.length > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleCategory(category.slug)}
                            className="text-xs text-dark-4 hover:text-dark transition-colors"
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? "▼" : "▶"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Subcategories Collapse */}
                    {isExpanded && categorySubcategories.length > 0 && (
                      <div className="mt-2 ml-6 space-y-1.5 pl-2 border-l border-gray-3">
                        {categorySubcategories.map((subcat) => {
                          const subcatId = `${p}subcat-${category.slug}-${subcat.slug}`;
                          return (
                            <div key={subcat.slug} className="flex items-center justify-between gap-2 text-xs text-dark-2">
                              <label htmlFor={subcatId} className="flex min-w-0 items-center gap-2 cursor-pointer hover:text-dark">
                                <input
                                  id={subcatId}
                                  type="checkbox"
                                  name="subcategories"
                                  value={subcat.slug}
                                  defaultChecked={selected.subcategories.includes(subcat.slug)}
                                  className="cursor-pointer"
                                />
                                <span className="truncate">{subcat.name}</span>
                              </label>
                              <span className="text-dark-4">{subcat.count}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        );

      case "usageArea":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-dark">{t("shop.usageArea")}</h3>
              <span className="text-xs text-dark-4">{usageAreas.length}</span>
            </div>
            <label htmlFor={`${p}usageArea-select`} className="sr-only">
              {t("shop.selectUsageArea")}
            </label>
            <select
              id={`${p}usageArea-select`}
              name="usageArea"
              defaultValue={selected.usageArea || ""}
              className="w-full rounded-md border border-gray-3 bg-white px-3 py-2 text-sm"
            >
              <option value="">{t("shop.all")}</option>
              {usageAreas.map((area) => (
                <option key={area.code} value={area.code}>
                  {area.name}
                </option>
              ))}
            </select>
          </div>
        );

      // The "Regional Product Names" card also holds the product-name search input (name="productName"):
      // in Shop that input belongs to this card, so the "region" key controls the card as a whole.
      case "region":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-dark">{t("shop.regionalProductNames")}</h3>
              <span className="text-xs text-dark-4">{filterOptions.regionNames.length}</span>
            </div>
            <div className="mb-3">
              <label htmlFor={`${p}productName-search`} className="mb-1 block text-xs font-semibold text-dark-3">
                {t("shop.productName")}
              </label>
              <input
                id={`${p}productName-search`}
                type="text"
                name="productName"
                defaultValue={selected.productName}
                placeholder={t("shop.productNameSearchPlaceholder")}
                className="w-full rounded-md border border-gray-3 bg-white px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-2 max-h-40 overflow-auto pr-1">
              {filterOptions.regionNames.map((item) => {
                const regionId = `${p}region-${item.slug}`;
                return (
                  <div key={`${item.slug}-${item.name}`} className="flex items-center justify-between gap-2 text-sm text-dark-2">
                    <label htmlFor={regionId} className="flex min-w-0 items-center gap-2 cursor-pointer hover:text-dark">
                      <input
                        id={regionId}
                        type="checkbox"
                        name="regionNames"
                        value={item.name}
                        defaultChecked={selected.regionNames.includes(item.name)}
                      />
                      <span className="truncate">{item.name}</span>
                    </label>
                    <span className="text-xs text-dark-4">{item.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "color":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-dark">{t("shop.colors")}</h3>
              <span className="text-xs text-dark-4">{filterOptions.colors.length}</span>
            </div>
            <div className="space-y-2 max-h-40 overflow-auto pr-1">
              {filterOptions.colors.map((item) => {
                const colorId = `${p}color-${item.id}`;
                return (
                  <div key={item.id} className="flex items-center justify-between gap-2 text-sm text-dark-2">
                    <label htmlFor={colorId} className="flex min-w-0 items-center gap-2 cursor-pointer hover:text-dark">
                      <input
                        id={colorId}
                        type="checkbox"
                        name="colorIds"
                        value={item.id}
                        defaultChecked={selected.colorIds.includes(item.id)}
                      />
                      <span className="truncate">{item.name}</span>
                    </label>
                    <span className="text-xs text-dark-4">{item.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "size":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-dark">{t("shop.sizes")}</h3>
              <span className="text-xs text-dark-4">{filterOptions.sizes.length}</span>
            </div>
            <div className="space-y-2">
              {filterOptions.sizes.map((item) => {
                const sizeId = `${p}size-${item.slug}`;
                return (
                  <div key={item.slug} className="flex items-center justify-between gap-2 text-sm text-dark-2">
                    <label htmlFor={sizeId} className="flex min-w-0 items-center gap-2 cursor-pointer hover:text-dark">
                      <input
                        id={sizeId}
                        type="checkbox"
                        name="sizes"
                        value={item.slug}
                        defaultChecked={selected.sizes.includes(item.slug)}
                      />
                      <span className="truncate">{item.name}</span>
                    </label>
                    <span className="text-xs text-dark-4">{item.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "price":
        return (
          <div className="rounded-lg border border-gray-3 bg-white p-3">
            <h3 className="text-sm font-semibold text-dark mb-2">{t("shop.priceRange")}</h3>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                name="minPrice"
                defaultValue={selected.minPrice}
                placeholder={`${Math.floor(filterOptions.minPrice)}`}
                className="w-full rounded-md border border-gray-3 bg-white px-3 py-2 text-sm"
              />
              <input
                type="number"
                name="maxPrice"
                defaultValue={selected.maxPrice}
                placeholder={`${Math.ceil(filterOptions.maxPrice)}`}
                className="w-full rounded-md border border-gray-3 bg-white px-3 py-2 text-sm"
              />
            </div>
          </div>
        );

      case "technicalProperties":
        return (
          filterOptions.technicalProperties.length > 0 && (
            <div className="rounded-lg border border-gray-3 bg-white p-3">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-dark">
                  {t("shop.technicalProperties")}
                </h3>
                <span className="text-xs text-dark-4">{filterOptions.technicalProperties.length}</span>
              </div>
              <div className="space-y-3 max-h-[360px] overflow-auto pr-1">
                {filterOptions.technicalProperties.map((property) => (
                  <fieldset key={property.slug} className="rounded-lg border border-gray-3 bg-white p-2">
                    <legend className="px-1 text-xs font-semibold text-dark-3">{property.name}</legend>
                    <div className="space-y-1.5 max-h-36 overflow-auto pr-1">
                      {property.isNumeric && property.minValue !== undefined && property.maxValue !== undefined ? (
                        <div className="px-1 py-2 space-y-2">
                          <div className="flex items-center gap-2 text-xs text-dark-2">
                            <label>{t("shop.min")}</label>
                            <input
                              type="number"
                              name={`tech.${property.slug}.min`}
                              min={property.minValue}
                              max={property.maxValue}
                              step="any"
                              defaultValue={selected.technical[property.slug]?.[0]?.split('-')[0]}
                              placeholder={String(property.minValue)}
                              className="w-16 rounded-md border border-gray-3 bg-white px-2 py-1 text-xs"
                            />
                          </div>
                          <div className="flex items-center gap-2 text-xs text-dark-2">
                            <label>{t("shop.max")}</label>
                            <input
                              type="number"
                              name={`tech.${property.slug}.max`}
                              min={property.minValue}
                              max={property.maxValue}
                              step="any"
                              defaultValue={selected.technical[property.slug]?.[1]?.split('-').pop()}
                              placeholder={String(property.maxValue)}
                              className="w-16 rounded-md border border-gray-3 bg-white px-2 py-1 text-xs"
                            />
                          </div>
                        </div>
                      ) : (
                        property.values.map((value) => (
                          <div key={`${property.slug}-${value}`} className="flex items-center justify-between gap-2 text-xs text-dark-2">
                            <label className="flex min-w-0 items-center gap-2 cursor-pointer hover:text-dark">
                              <input
                                type="checkbox"
                                name={`tech.${property.slug}`}
                                value={value}
                                defaultChecked={(selected.technical[property.slug] || []).includes(value)}
                              />
                              <span className="truncate">{value}</span>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </fieldset>
                ))}
              </div>
            </div>
          )
        );

      // Home-only registry keys (productName) and the nested "subcategory" have no standalone Shop control.
      default:
        return null;
    }
  };

  const desktopApply = filterButtonAttributes(
    buttonDesign,
    "rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2",
  );
  const mobileApply = filterButtonAttributes(
    buttonDesign,
    "rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2",
  );

  return (
    <section className="relative px-4 py-12 sm:px-6 lg:px-8 bg-white" aria-label="shop with sidebar">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/35 backdrop-blur-[1px]">
          <div className="rounded-xl border border-gray-3 bg-white/90 px-4 py-3 shadow-1 flex items-center gap-3">
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-blue border-t-transparent" />
            <span className="text-sm font-medium text-dark">{t("shop.loading")}</span>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-7xl grid gap-6 lg:grid-cols-[300px_1fr]">
        {/* Mobile Filter Toggle Button */}
        <div className="lg:hidden flex items-center gap-2 mb-4">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-md font-semibold hover:bg-primary-dark transition-colors w-full justify-center"
            aria-label={t("shop.toggleFilter")}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 2H17C17.55 2 18 2.45 18 3C18 3.55 17.55 4 17 4H3C2.45 4 2 3.55 2 3C2 2.45 2.45 2 3 2Z" fill="currentColor"/>
              <path d="M4 9H16C16.55 9 17 9.45 17 10C17 10.55 16.55 11 16 11H4C3.45 11 3 10.55 3 10C3 9.45 3.45 9 4 9Z" fill="currentColor"/>
              <path d="M5 16H15C15.55 16 16 16.45 16 17C16 17.55 15.55 18 15 18H5C4.45 18 4 17.55 4 17C4 16.45 4.45 16 5 16Z" fill="currentColor"/>
            </svg>
            {t("shop.filter")}
          </button>
        </div>

        {/* Desktop Sidebar (hidden on mobile) */}
        <aside className="hidden lg:block rounded-xl border border-gray-3 bg-gray-1 p-4 lg:sticky lg:top-24 lg:h-fit">
          <form method="get" action={cleanUrl} onSubmit={handleFormSubmit} className="space-y-4">
            <div className="px-5 py-4 bg-white rounded-lg shadow-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-dark">{t("shop.filters")}</p>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="text-blue text-sm font-semibold hover:underline"
                >
                  {t("shop.cleanAll")}
                </button>
              </div>
            </div>

            {layout.keys.map((key) => (
              <Fragment key={key}>{renderShopFilter(key, "desktop")}</Fragment>
            ))}

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2 pt-2 relative">
              <button
                type="submit"
                disabled={isLoading}
                className={desktopApply.className}
                style={desktopApply.style}
              >
                {isLoading && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
                {t("shop.apply")}
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="rounded-md border border-gray-3 bg-white px-4 py-2 text-center text-sm font-semibold text-dark hover:bg-gray-2 transition-colors"
              >
                {t("shop.reset")}
              </button>
            </div>
          </form>
        </aside>

        {/* Mobile Filter Modal */}
        {isFilterOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm"
              onClick={closeFilter}
            />
            {/* Modal */}
            <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <form method="get" action={cleanUrl} onSubmit={(e) => {
                handleFormSubmit(e);
                closeFilter();
              }} className="space-y-4 p-4">
                {/* Header */}
                <div className="flex items-center justify-between sticky top-0 bg-white pb-4 border-b border-gray-3 -mx-4 px-4">
                  <h2 className="text-lg font-semibold text-dark">{t("shop.filterBy")}</h2>
                  <button
                    type="button"
                    onClick={closeFilter}
                    className="text-dark-3 hover:text-dark text-2xl leading-none"
                    aria-label={t("shop.closeFilter")}
                  >
                    ×
                  </button>
                </div>

                {/* Clear Button */}
                <div className="px-1">
                  <button
                    type="button"
                    onClick={() => {
                      handleClearFilters();
                      closeFilter();
                    }}
                    className="text-primary text-sm font-semibold hover:underline"
                  >
                    {t("shop.clearAll")}
                  </button>
                </div>

                {layout.keys.map((key) => (
                  <Fragment key={key}>{renderShopFilter(key, "mobile")}</Fragment>
                ))}

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 pt-2 sticky bottom-0 bg-white pb-4 -mx-4 px-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={mobileApply.className}
                    style={mobileApply.style}
                  >
                    {isLoading && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
                    {t("shop.apply")}
                  </button>
                  <button
                    type="button"
                    onClick={closeFilter}
                    className="rounded-md border border-gray-3 bg-white px-4 py-2.5 text-center text-sm font-semibold text-dark hover:bg-gray-2 transition-colors"
                  >
                    {t("common.close")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div>
          <div className="rounded-lg bg-white shadow-1 pl-3 pr-2.5 py-2.5 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative custom-select custom-select-2 shrink-0">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as "latest" | "best")}
                    className="select-selected whitespace-nowrap text-left min-w-[152px] !text-dark-4 !font-normal rounded-md border border-gray-3 bg-white px-3 py-2"
                    aria-label={t("shop.productSorting")}
                  >
                    <option value="latest">{t("shop.sortLatest")}</option>
                    <option value="best">{t("shop.sortBest")}</option>
                  </select>
                </div>
                <p className="text-sm text-dark-3">
                  {t("shop.showing")}
                  <span className="text-dark"> {displayedProducts.length} </span>
                  {t("shop.of")} {displayedProducts.length} {t("shop.products")}
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <button
                  aria-label={t("shop.gridView")}
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={`${viewMode === "grid" ? "bg-blue border-blue text-white" : "text-dark bg-gray-1 border-gray-3"} flex items-center justify-center w-10.5 h-9 rounded-lg border ease-out duration-200 hover:bg-blue hover:border-blue hover:text-white`}
                >
                  <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M5.60352 3.25C4.36088 3.25 3.35352 4.25736 3.35352 5.5V8.99998C3.35352 10.2426 4.36087 11.25 5.60352 11.25H9.10352C10.3462 11.25 11.3535 10.2426 11.3535 8.99998V5.5C11.3535 4.25736 10.3462 3.25 9.10352 3.25H5.60352ZM4.85352 5.5C4.85352 5.08579 5.1893 4.75 5.60352 4.75H9.10352C9.51773 4.75 9.85352 5.08579 9.85352 5.5V8.99998C9.85352 9.41419 9.51773 9.74998 9.10352 9.74998H5.60352C5.1893 9.74998 4.85352 9.41419 4.85352 8.99998V5.5Z" fill="currentColor"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M5.60352 12.75C4.36088 12.75 3.35352 13.7574 3.35352 15V18.5C3.35352 19.7426 4.36087 20.75 5.60352 20.75H9.10352C10.3462 20.75 11.3535 19.7426 11.3535 18.5V15C11.3535 13.7574 10.3462 12.75 9.10352 12.75H5.60352ZM4.85352 15C4.85352 14.5858 5.1893 14.25 5.60352 14.25H9.10352C9.51773 14.25 9.85352 14.5858 9.85352 15V18.5C9.85352 18.9142 9.51773 19.25 9.10352 19.25H5.60352C5.1893 19.25 4.85352 18.9142 4.85352 18.5V15Z" fill="currentColor"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M12.8535 5.5C12.8535 4.25736 13.8609 3.25 15.1035 3.25H18.6035C19.8462 3.25 20.8535 4.25736 20.8535 5.5V8.99998C20.8535 10.2426 19.8462 11.25 18.6035 11.25H15.1035C13.8609 11.25 12.8535 10.2426 12.8535 8.99998V5.5ZM15.1035 4.75C14.6893 4.75 14.3535 5.08579 14.3535 5.5V8.99998C14.3535 9.41419 14.6893 9.74998 15.1035 9.74998H18.6035C19.0177 9.74998 19.3535 9.41419 19.3535 8.99998V5.5C19.3535 5.08579 19.0177 4.75 18.6035 4.75H15.1035Z" fill="currentColor"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M15.1035 12.75C13.8609 12.75 12.8535 13.7574 12.8535 15V18.5C12.8535 19.7426 13.8609 20.75 15.1035 20.75H18.6035C19.8462 20.75 20.8535 19.7426 20.8535 18.5V15C20.8535 13.7574 19.8462 12.75 18.6035 12.75H15.1035ZM14.3535 15C14.3535 14.5858 14.6893 14.25 15.1035 14.25H18.6035C19.0177 14.25 19.3535 14.5858 19.3535 15V18.5C19.3535 18.9142 19.0177 19.25 18.6035 19.25H15.1035C14.6893 19.25 14.3535 18.9142 14.3535 18.5V15Z" fill="currentColor"/>
                  </svg>
                </button>
                <button
                  aria-label={t("shop.listView")}
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={`${viewMode === "list" ? "bg-blue border-blue text-white" : "text-dark bg-gray-1 border-gray-3"} flex items-center justify-center w-10.5 h-9 rounded-lg border ease-out duration-200 hover:bg-blue hover:border-blue hover:text-white`}
                >
                  <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M4.25 4.5C3.2835 4.5 2.5 5.2835 2.5 6.25V9.5C2.5 10.4665 3.2835 11.25 4.25 11.25H7.5C8.4665 11.25 9.25 10.4665 9.25 9.5V6.25C9.25 5.2835 8.4665 4.5 7.5 4.5H4.25ZM4 6.25C4 6.11193 4.11193 6 4.25 6H7.5C7.63807 6 7.75 6.11193 7.75 6.25V9.5C7.75 9.63807 7.63807 9.75 7.5 9.75H4.25C4.11193 9.75 4 9.63807 4 9.5V6.25Z" fill="currentColor"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M4.25 12.75C3.2835 12.75 2.5 13.5335 2.5 14.5V17.75C2.5 18.7165 3.2835 19.5 4.25 19.5H7.5C8.4665 19.5 9.25 18.7165 9.25 17.75V14.5C9.25 13.5335 8.4665 12.75 7.5 12.75H4.25ZM4 14.5C4 14.3619 4.11193 14.25 4.25 14.25H7.5C7.63807 14.25 7.75 14.3619 7.75 14.5V17.75C7.75 17.8881 7.63807 18 7.5 18H4.25C4.11193 18 4 17.8881 4 17.75V14.5Z" fill="currentColor"/>
                    <path d="M10.75 6.375C10.75 5.96079 11.0858 5.625 11.5 5.625H21.75C22.1642 5.625 22.5 5.96079 22.5 6.375C22.5 6.78921 22.1642 7.125 21.75 7.125H11.5C11.0858 7.125 10.75 6.78921 10.75 6.375Z" fill="currentColor"/>
                    <path d="M11.5 8.625C11.0858 8.625 10.75 8.96079 10.75 9.375C10.75 9.78921 11.0858 10.125 11.5 10.125H21.75C22.1642 10.125 22.5 9.78921 22.5 9.375C22.5 8.96079 22.1642 8.625 21.75 8.625H11.5Z" fill="currentColor"/>
                    <path d="M10.75 14.625C10.75 14.2108 11.0858 13.875 11.5 13.875H21.75C22.1642 13.875 22.5 14.2108 22.5 14.625C22.5 15.0392 22.1642 15.375 21.75 15.375H11.5C11.0858 15.375 10.75 15.0392 10.75 14.625Z" fill="currentColor"/>
                    <path d="M11.5 16.875C11.0858 16.875 10.75 17.2108 10.75 17.625C10.75 18.0392 11.0858 18.375 11.5 18.375H21.75C22.1642 18.375 22.5 18.0392 22.5 17.625C22.5 17.2108 22.1642 16.875 21.75 16.875H11.5Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {displayedProducts.length === 0 ? (
            <div className="rounded-xl border border-gray-3 bg-gray-1 p-8 text-center text-dark-3">
              {t("shop.noProductsFound")}
            </div>
          ) : (
            <div className={viewMode === "grid" ? "grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3" : "grid grid-cols-1 gap-4"}>
              {displayedProducts.map((product) => {
                return (
                  <article key={product.slug} className={`rounded-xl border border-gray-3 bg-white p-3 shadow-1 ${viewMode === "list" ? "sm:flex sm:items-center sm:gap-4" : ""}`}>
                    <div className="aspect-[4/3] overflow-hidden rounded-lg bg-gray-1">
                      <SafeImage
                        src={product.imageUrl}
                        fallbackType={product.imageFallbackType}
                        defaults={defaults}
                        alt={product.imageAlt}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="mt-3 space-y-2">
                      <Link href={product.href} className="block text-base font-semibold text-dark hover:text-primary">
                        {product.name}
                      </Link>
                      {product.price ? (
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold text-primary">{product.price.formatted}</span>
                          {product.price.hasReducedPrice ? (
                            <span className="text-xs text-dark-4 line-through">{product.price.formattedOriginal}</span>
                          ) : null}
                        </div>
                      ) : null}
                      {/* The explicit Product Detail action (a link — never a cart action): absent while the domain hides it. It is a sibling of
                          the title link, never inside it. */}
                      {product.action ? (
                        <Link href={product.action.href} className="sf-cta">
                          {product.action.label}
                        </Link>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
